import { useState } from "react";
import { useParams } from "react-router-dom";

import { describeApiError } from "../../api/errors";
import { type ConsentStart, startHmrcConsent, submitFraudSignals } from "../../api/hmrcFraudSignals";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { Page } from "../../components/Page";
import { t } from "../../i18n/t";

/**
 * The console half of §G5.3, and the ONLY place a VRN enters the system (R-17.8, round-6 M-12).
 *
 * Two clicks, and the order is the compliance requirement. §G2.1 and §G2.5 oblige us to tell the operator
 * why we ask for `write:vat` and that HMRC forbids us ever holding their Government Gateway credentials -
 * BEFORE they are sent to HMRC. So click one starts the flow and renders `ConsentStart.notices`, which the
 * API returns as data (R-17.3) precisely so the copy stays testable in Python; click two collects the seven
 * browser values, posts them bound to the `state`, and only then destroys the page.
 *
 * **The VRN is validated here and again on the server.** Nine digits is HMRC's own `vrn` shape. It is never
 * put in a URL fragment, in `sessionStorage` or in a toast: §A2.n and §G5's redaction rule make a VRN in a
 * message a defect, so the refusal names the field.
 *
 * **It takes NO PROPS and reads `migrationId` from the ROUTE** (v7 **§Z item 85**, §G5.3 consequence 1).
 * Task 20's land is ONE line in `web/src/screens/connect/routes.tsx` and a route element cannot supply a
 * required prop: with the v7 signature, `tsc --noEmit` is
 * `TS2741: Property 'migrationId' is missing in type '{}' but required in type '{ migrationId: string; }'`
 * on that very line (measured, exit 2). `useParams()` plus `?? ""` is the landed convention verbatim -
 * `DryRunScreen`, `ScopeScreen`, `StatusScreen` and `ReviewScreen` all open this way - and the segment is
 * **`:migrationId`**, camelCase, which is how all five `routes.tsx` files and `web/src/router.tsx` spell it.
 * A snake-case `:migration_id` would leave this hook returning `undefined` on a screen that mounts, which is
 * worse than the prop it replaces.
 */
const VRN_SHAPE = /^[0-9]{9}$/;

export function HmrcConsentForm(): JSX.Element {
  const { migrationId } = useParams();
  const id = migrationId ?? "";
  const [vrn, setVrn] = useState("");
  const [start, setStart] = useState<ConsentStart | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onStart(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    if (!VRN_SHAPE.test(vrn)) {
      setError(t("connect.hmrc_vrn_invalid"));
      return;
    }
    setBusy(true);
    try {
      setStart(await startHmrcConsent(id, vrn));
    } catch (caught) {
      setError(describeApiError(caught, t("error.unexpected")));
    } finally {
      setBusy(false);
    }
  }

  async function onContinue(): Promise<void> {
    if (start === null) {
      return;
    }
    setError(null);
    setBusy(true);
    try {
      // The attestation FIRST: a callback whose `state` has no stored `fph_blob` is a 409 that completes
      // no exchange, so a failure here must stop the flow rather than send the operator to HMRC anyway.
      await submitFraudSignals(start.state, start.required_headers);
      window.location.assign(start.authorize_url);
    } catch (caught) {
      setError(describeApiError(caught, t("error.unexpected")));
      setBusy(false);
    }
  }

  return (
    <Page title={t("connect.hmrc_title")}>
      <section className="card">
        <form onSubmit={(event) => void onStart(event)}>
          <Field
            label={t("connect.hmrc_vrn")}
            hint={t("connect.hmrc_vrn_hint")}
            error={start === null ? error : null}
            required
          >
            {({ id, describedBy, required }) => (
              <input
                id={id}
                inputMode="numeric"
                autoComplete="off"
                required={required}
                aria-describedby={describedBy}
                value={vrn}
                onChange={(event) => setVrn(event.target.value)}
                disabled={start !== null}
              />
            )}
          </Field>
          {start === null ? (
            <Button type="submit" disabled={busy}>
              {t("connect.hmrc_submit")}
            </Button>
          ) : null}
        </form>
        {start === null ? null : (
          <>
            {start.notices.map((notice) => (
              <Banner key={notice} tone="info">
                {notice}
              </Banner>
            ))}
            {error === null ? null : <Banner tone="block">{error}</Banner>}
            <Button onClick={() => void onContinue()} disabled={busy}>
              {t("connect.hmrc_continue")}
            </Button>
          </>
        )}
      </section>
    </Page>
  );
}
